import { useEffect, useMemo, useState } from "react";

import { Stack, Typography } from "@mui/material";
import { isNil } from "lodash";

import {
  useAPI,
  useRoleContext,
  useShipmentQuotes,
  useSubscriptionStatus,
} from "hooks";
import {
  carrierHasBookingWriteAccess,
  shouldShowShipperCompanyName,
} from "utils";

import { getShipmentSubheader } from "./utils";
import { CompanyBusinessTypeChip } from "../../../CompanyBusinessTypeChip";
import { CompanyNameUpgradeText } from "../CompanyNameUpgradeText";
import { ShipperBookingSpeedIndicator } from "../ShipperBookingSpeedIndicator";
import { ShipperBookingSpeedTeaser } from "../ShipperBookingSpeedIndicator/ShipperBookingSpeedTeaser";

interface Props {
  shipment: CondensedShipment;
}

export function ShipmentOverviewSubheader(props: Props) {
  const { shipment } = props;
  const { api } = useAPI();
  const [followed, setFollowed] = useState<FollowedShipper[]>([]);
  const { role } = useRoleContext();
  const { isPayingCarrier } = useSubscriptionStatus();
  const [loading, setLoading] = useState<boolean>(true);

  const { quotes, isLoading } = useShipmentQuotes(shipment.info.id);

  const bookedByCarrier = useMemo(() => {
    if (!isLoading) {
      const bookedQuote = quotes.find(
        (quote) => quote.internal_state === "ACCEPTED",
      );

      return (
        bookedQuote !== undefined ||
        carrierHasBookingWriteAccess(shipment as CarrierCondensedShipment)
      );
    }

    return false;
  }, [quotes, isLoading, shipment]);

  const isPartnerShipment = !isNil(
    followed.find(
      (shipper) => shipper?.user?.id === shipment?.shipment_creator?.id,
    ),
  );
  const isSubscribed = Boolean(isPayingCarrier);
  const showCompanyName = shouldShowShipperCompanyName({
    isSubscribed,
    isPartnerShipment,
    isBookedByCarrier: bookedByCarrier,
  });

  useEffect(() => {
    if (loading && role) {
      api<FollowedShipper[]>(
        "get",
        `/carriers/${role.id}/followed_shippers/following`,
      )
        .then((response) => {
          setFollowed(response.data);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [api, role, loading]);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {getShipmentSubheader(
          shipment,
          isPartnerShipment,
          bookedByCarrier,
          isSubscribed,
        )}
        {!showCompanyName && (
          <>
            {" "}
            <CompanyNameUpgradeText />
          </>
        )}
      </Typography>
      <CompanyBusinessTypeChip
        businessType={shipment.shipment_company.business_type}
      />
      {isSubscribed ? (
        <ShipperBookingSpeedIndicator shipperRoleId={shipment.info.role} />
      ) : (
        <ShipperBookingSpeedTeaser shipperRoleId={shipment.info.role} />
      )}
    </Stack>
  );
}
